// Helpers del tab Portfolio (vista cruzada de la empresa): normaliza las filas que envía el
// backend ({project, kpi}), calcula los semáforos por criterio y los agregados de cabecera y
// por grupo de proyecto. Reutiliza los mismos umbrales que el cockpit por proyecto (avance/margen) y el
// modelo de riesgo del Resumen (I×P ≥ 10 = crítico), para que una fila del Portfolio coincida
// con el propio Dashboard del proyecto.

import { progressColor, marginColor } from '../dashboard/dashboard-model'
import { RISK_CRITICAL, scoreColor } from '../summary/summary-model'

const num = (v) => (v == null || v === '' || isNaN(Number(v)) ? null : Number(v))
const z = (v) => Number(v) || 0
const sum = (rows, sel) => rows.reduce((s, r) => s + (sel(r) || 0), 0)

// Aplana una fila del backend {project, kpi} a la forma que usan las tablas del Portfolio.
export function normalize (row) {
  const p = row.project || {}
  const k = row.kpi || {}
  const prm = k.prm || null
  return {
    id: p.id,
    code: p.reference || '',
    name: p.name || '',
    // Grupo de proyecto (agrupación del Portfolio). El backend expone el primer grupo del proyecto
    // en p.groups (ver Project::mapGroups); vacío/gris si el proyecto no pertenece a ninguno.
    group: (p.groups && p.groups[0] && p.groups[0].name) || '',
    groupColor: groupColor(p.groups && p.groups[0] ? p.groups[0].id : null), // color real (como /select-group)
    client: k.client || '',
    pm: k.pm || '',
    actualProgress: num(k.progressReal),
    plannedProgress: num(k.progressScheduled),
    hasPrm: prm != null,
    commercialMargin: prm ? num(prm.commercialMargin) : null, // fracción
    actualMargin: prm ? num(prm.actualMargin) : null,         // fracción
    totalAmount: prm ? num(prm.totalAmount) : null,
    contractAmount: prm ? num(prm.contractAmount) : null,
    changeOrders: prm ? num(prm.changeOrders) : null,
    poc: prm ? num(prm.poc) : null,                           // fracción
    riskScore: z(k.riskScore),
    riskImpact: z(k.riskImpact),
    riskProbability: z(k.riskProbability),
    oshOpen: z(k.oshOpen),
    billing: k.billing || { hasData: false, invoiced: 0, overdue: 0, ratio: 0 }, // facturación cliente (S03)
    contractualRed: z(k.contractualRed),                     // nº de elementos contractuales en rojo
    criticalIncidents: z(k.criticalIncidents),               // incidencias críticas abiertas
    project: p,                                               // objeto completo para abrir el proyecto
  }
}

// Semáforo de facturación: rojo si vencidas > 5 % de lo facturado, ámbar > 1 %, verde si no; null sin datos.
export function semBilling (r) {
  const b = r.billing || {}
  if (!b.hasData) return null
  if (z(b.ratio) > 0.05) return 'red'
  if (z(b.ratio) > 0.01) return 'amber'
  return 'green'
}

// Desviación de avance (pp) = real − plan; null si no hay avance registrado.
export function progressDev (r) {
  if (r.actualProgress == null && r.plannedProgress == null) return null
  return z(r.actualProgress) - z(r.plannedProgress)
}

// Desviación de margen (pp) = (margen actual − comercial) × 100; null si no hay PRM.
export function marginDevPp (r) {
  return r.hasPrm ? (z(r.actualMargin) - z(r.commercialMargin)) * 100 : null
}

// Nivel de semáforo de un score de riesgo contractual (verde <5, ámbar 5-9, rojo ≥10).
export function riskLevel (score) {
  return scoreColor(score) // reutiliza el mismo umbral del Resumen
}

// Color del grupo de proyecto: MISMA paleta y lógica que /select-group (colors[group.id % N]),
// para que el chip del Portfolio coincida con el color que ya se ve allí. Gris si no hay grupo.
const GROUP_PALETTE = ['#2563eb', '#16a34a', '#7c3aed', '#ea580c', '#0d9488']
export function groupColor (id) {
  if (id == null || id === '') return '#94a3b8' // gris para "Sin grupo"
  return GROUP_PALETTE[Number(id) % GROUP_PALETTE.length]
}

// ─── Derivados por fila ───────────────────────────────────────────────────────

// Backlog € = importe total × (1 − avance real). Igual que la ficha del Dashboard.
export function backlog (r) {
  return r.hasPrm ? z(r.totalAmount) * (1 - z(r.actualProgress) / 100) : null
}

// Desviación de margen € = margen actual € − margen comercial € (mismos importes que el Dashboard).
export function marginDev (r) {
  return r.hasPrm ? z(r.actualMargin) * z(r.totalAmount) - z(r.commercialMargin) * z(r.contractAmount) : null
}

// Semáforo de avance (null si el proyecto no tiene avance registrado → celda neutra).
export function semProgress (r) {
  if (r.actualProgress == null && r.plannedProgress == null) return null
  return progressColor(r.actualProgress, r.plannedProgress)
}

// Semáforo de margen (null si el proyecto aún no tiene PRM).
export function semMargin (r) {
  return r.hasPrm ? marginColor(r.commercialMargin, r.actualMargin) : null
}

// Riesgo contractual crítico: mayor riesgo abierto I×P ≥ 10.
export function riskCritical (r) {
  return z(r.riskScore) >= RISK_CRITICAL
}

// ¿La fila tiene algún dato real? (si no, el estado general se muestra neutro, no verde).
function hasData (r) {
  return r.hasPrm || r.actualProgress != null || r.plannedProgress != null ||
    z(r.riskScore) > 0 || z(r.oshOpen) > 0 || z(r.contractualRed) > 0 ||
    z(r.criticalIncidents) > 0 || (r.billing && r.billing.hasData)
}

// Estado general (criterio ** del documento): rojo si se cumple al menos un criterio de situación
// crítica (avance, margen, facturación, contractual, safety o calidad); ámbar si hay alertas
// moderadas; verde si no hay alertas.
export function semState (r) {
  if (!hasData(r)) return null
  const a = semProgress(r)
  const m = semMargin(r)
  const b = semBilling(r)
  if (a === 'red' || m === 'red' || b === 'red' || z(r.contractualRed) > 0 ||
      z(r.oshOpen) >= 2 || z(r.criticalIncidents) > 0) return 'red'
  if (a === 'amber' || m === 'amber' || b === 'amber' || z(r.oshOpen) > 0) return 'amber'
  return 'green'
}

// ─── Agregados de cabecera (medias ponderadas por total de venta) ─────────────

export function portfolioKpis (rows) {
  const withPrm = rows.filter((r) => r.hasPrm)
  const totalSale = sum(withPrm, (r) => z(r.totalAmount))
  const wavg = (sel) => (totalSale ? withPrm.reduce((s, r) => s + sel(r) * z(r.totalAmount), 0) / totalSale : 0)
  return {
    numProjects: rows.length,
    totalSale,
    backlogTotal: sum(withPrm, (r) => backlog(r) || 0),
    avgCommercialMargin: wavg((r) => z(r.commercialMargin)), // fracción
    avgActualMargin: wavg((r) => z(r.actualMargin)),         // fracción
    marginDevTotal: sum(withPrm, (r) => marginDev(r) || 0),
    changeOrdersTotal: sum(withPrm, (r) => z(r.changeOrders)),
    avgPoc: wavg((r) => z(r.poc)),                           // fracción
    criticalProjects: rows.filter((r) => semState(r) === 'red').length,
    contractualRiskProjects: rows.filter((r) => riskCritical(r)).length,
    oshProjects: rows.filter((r) => z(r.oshOpen) > 0).length,
  }
}

// ─── Agregados por grupo de proyecto (sustituye a la "línea de negocio" del demo) ──────────

export function groupAggregates (rows) {
  const byGroup = {}
  rows.forEach((r) => {
    const key = r.group || '' // los proyectos sin grupo van juntos ("Sin grupo")
    ;(byGroup[key] || (byGroup[key] = [])).push(r)
  })
  return Object.keys(byGroup)
    .sort((a, b) => a.localeCompare(b, undefined, { numeric: true, sensitivity: 'base' }))
    .map((group) => {
      const items = byGroup[group]
      const withPrm = items.filter((r) => r.hasPrm)
      const totalSale = sum(withPrm, (r) => z(r.totalAmount))
      const wavg = (sel) => (totalSale ? withPrm.reduce((s, r) => s + sel(r) * z(r.totalAmount), 0) / totalSale : 0)
      return {
        group,
        color: items[0] ? items[0].groupColor : '#94a3b8', // color del grupo (todas sus filas comparten grupo)
        numProjects: items.length,
        totalSale,
        backlog: sum(withPrm, (r) => backlog(r) || 0),
        changeOrders: sum(withPrm, (r) => z(r.changeOrders)),
        avgCommercialMargin: wavg((r) => z(r.commercialMargin)),
        avgActualMargin: wavg((r) => z(r.actualMargin)),
        marginDev: sum(withPrm, (r) => marginDev(r) || 0),
        avgPoc: wavg((r) => z(r.poc)),
        green: items.filter((r) => semState(r) === 'green').length,
        amber: items.filter((r) => semState(r) === 'amber').length,
        red: items.filter((r) => semState(r) === 'red').length,
      }
    })
}
